import defaultStyled, { css as defaultCss, DefaultTheme } from 'styled-components';
import { Color, Theme } from 'src/utils/theme';
import createVariablesWrapper from 'src/components/createVariablesWrapper';
import IconComponent from 'src/components/Icon';
import { active, hover } from 'src/components/utils';

export const ButtonColor = {
  GREEN: 'green',
  GREY: 'grey',
  YELLOW: 'yellow',
} as const;
export type ButtonColor = (typeof ButtonColor)[keyof typeof ButtonColor];

export type ButtonStyleProps = {
  $buttonColor: ButtonColor;
  $vertical: boolean;
};

type ButtonStyleVariables = {
  fontSize: string;
  borderRadius: string;
  width: string;
  height: string;
  padding: string;
  backgroundColor: string;
  hoverBackgroundColor: string;
  activeBackgroundColor: string;
  borderColor: string;
  labelColor: string;
  fontColor: string;
  hoverFontColor: string;
  activeFontColor: string;
  animationTiming: string;
};

type ButtonTheme = DefaultTheme & ButtonStyleVariables;

const styled = defaultStyled;
const css = defaultCss;

export type Selector = 'hover' | 'active';

type ApplySelector = (selector: Selector | null | undefined) => (color: string) => string;
const applySelector: ApplySelector = selector => color => {
  switch (selector) {
    case 'active':
      return active(color);
    case 'hover':
      return hover(color);
    case null:
      return color;
    case undefined:
      return color;
  }
};

type BaseColors = Record<
  'fontPrimary' | 'background' | 'border' | 'label',
  Record<ButtonColor, Color>
>;

const baseColors: BaseColors = {
  fontPrimary: {
    [ButtonColor.GREEN]: 'secondary0',
    [ButtonColor.GREY]: 'neutral20',
    [ButtonColor.YELLOW]: 'secondary0',
  },
  background: {
    [ButtonColor.GREEN]: 'success40',
    [ButtonColor.GREY]: 'neutral90',
    [ButtonColor.YELLOW]: 'primary20',
  },
  border: {
    [ButtonColor.GREEN]: 'success40',
    [ButtonColor.GREY]: 'secondary80',
    [ButtonColor.YELLOW]: 'primary20',
  },
  label: {
    [ButtonColor.GREEN]: 'success40',
    [ButtonColor.GREY]: 'neutral20',
    [ButtonColor.YELLOW]: 'primary20',
  },
};

// Base/simple getters (those that use only color + props)

type BaseVarGetter = (theme: DefaultTheme) => (styleProps: ButtonStyleProps) => string;

const colorAccessor =
  (key: keyof BaseColors): BaseVarGetter =>
  ({ co }) =>
  ({ $buttonColor }) =>
    co[baseColors[key][$buttonColor]];

// Complex getters (those that also rely upon selectors)

type VarGetter = (
  theme: Theme,
) => (styleProps: ButtonStyleProps) => (selector?: Selector) => string;

export const getFontColor: VarGetter = theme => styleProps => selector => {
  const baseFontColor = colorAccessor('fontPrimary')(theme)(styleProps);
  return applySelector(selector)(baseFontColor);
};

export const getBackgroundColor: VarGetter = theme => styleProps => selector => {
  const baseBackgroundColor = colorAccessor('background')(theme)(styleProps);
  return applySelector(selector)(baseBackgroundColor);
};

export const Variables = createVariablesWrapper<ButtonStyleProps, ButtonStyleVariables>(
  styleProps => theme => {
    const { $vertical } = styleProps;
    const fontSize = $vertical ? theme.DEPRECATED_ty.small : theme.DEPRECATED_ty.medium;
    const borderRadius = theme.bo.small;
    const width = '100%';
    const height = 'auto';
    const padding = $vertical ? `${theme.sz.s4} ${theme.sz.s2}` : theme.sz.s4;

    const getThemedFontColor = getFontColor(theme)(styleProps);
    const fontColor = getThemedFontColor();
    const activeFontColor = getThemedFontColor('active');
    const hoverFontColor = getThemedFontColor('hover');

    const getThemedBGColor = getBackgroundColor(theme)(styleProps);
    const backgroundColor = getThemedBGColor();
    const activeBackgroundColor = getThemedBGColor('active');
    const hoverBackgroundColor = getThemedBGColor('hover');

    const borderColor = colorAccessor('border')(theme)(styleProps);
    const labelColor = colorAccessor('label')(theme)(styleProps);

    return {
      fontSize,
      borderRadius,
      width,
      height,
      padding,
      backgroundColor,
      activeBackgroundColor,
      hoverBackgroundColor,
      borderColor,
      labelColor,
      fontColor,
      activeFontColor,
      hoverFontColor,
      animationTiming: '200ms cubic-bezier(0, 1, 0.75, 1)',
      ...theme,
    };
  },
);

interface ButtonProps {
  $vertical: boolean;
}

const buttonCss = css<ButtonProps>`
  /* Shared Styles */
  align-items: center;
  background: ${({ theme }) => (theme as ButtonTheme).backgroundColor};
  border-radius: ${({ theme }) => (theme as ButtonTheme).borderRadius};
  color: ${({ theme }) => (theme as ButtonTheme).fontColor};
  cursor: pointer;
  display: flex;
  flex-basis: 100%;
  flex-direction: ${({ $vertical }) => ($vertical ? 'column' : 'row')};
  font-family: ${({ theme }) => theme.DEPRECATED_ty.openSans};
  font-size: ${({ theme }) => (theme as ButtonTheme).fontSize};
  hyphens: auto;
  justify-content: center;
  line-height: 1.2;
  overflow-wrap: anywhere;
  padding: ${({ theme }) => (theme as ButtonTheme).padding};
  position: relative;
  text-align: center;
  text-decoration: none;
  transition:
    opacity ${({ theme }) => (theme as ButtonTheme).animationTiming},
    color 0.2s 0.1s ease-in-out;
  width: ${({ theme }) => (theme as ButtonTheme).width};
  height: ${({ theme }) => (theme as ButtonTheme).height};
  z-index: ${({ theme }) => theme.zi.z3};

  &:hover,
  &:hover:visited {
    background-color: ${({ theme }) => (theme as ButtonTheme).hoverBackgroundColor};
    color: ${({ theme }) => theme.co.secondary0};
  }

  &:active,
  &:active:visited {
    box-shadow: none;
    color: ${({ theme }) => theme.co.secondary0};
    background-color: ${({ theme }) => (theme as ButtonTheme).activeBackgroundColor};
  }

  border: none;
  box-shadow: inset 0 -4px 0 rgba(0, 0, 0, 10%);
  font-weight: ${({ theme }) => theme.DEPRECATED_ty.bold};
`;

export const Button = styled.button<ButtonProps>`
  ${buttonCss}
`;

export const Container = styled.div<{ $vertical: boolean }>`
  ${({ theme, $vertical }) => !$vertical && `flex-basis: ${(theme as ButtonTheme).width};`}
  z-index: ${props => props.theme.zi.z1};
  position: relative;
`;

export const Icon = styled(IconComponent)<{
  $vertical: boolean;
}>(
  ({ theme, $vertical }) => css`
    display: inline-flex;
    align-items: center;

    ${$vertical ? `margin-bottom: ${theme.sz.s4}` : `margin: 0 ${theme.sz.s2}`};

    &,
    svg {
      width: ${$vertical ? theme.sz.s8 : theme.sz.s6};
      height: ${$vertical ? theme.sz.s8 : theme.sz.s6};
    }

    & svg * {
      fill: ${(theme as ButtonTheme).fontColor};
    }
  `,
);
